"use client";

import type { RadioInputStepInput } from "../form-types";
import type { StepInputRendererProps } from "../StepInputRenderer";

import { cn } from "@homeinstead/ui";
import { useEffect, useId, useRef } from "react";
import { usePrefersReducedMotion } from "~www/hooks/use-prefers-reduced-motion";
import {
  resolveLabelFontFamilyClass,
  resolveTextSizeClass,
} from "../form-item-styles";
import { defaultHeroVariantStyles } from "../hero-variant-styles";
import { RadioInputOption } from "../RadioInputOption";
import { usePersistedFieldState } from "../use-persisted-field-state";
import { useTrackLeadFormEvent } from "../useTrackLeadFormEvent";
import { ButtonStackCheckBadge } from "./ButtonStackCheckBadge";

/**
 * The check stroke ends at 800ms. The step waits until 1000ms so the finished
 * check is on screen before the transition. Keep the stroke timing aligned
 * with button-stack-motion.css.
 */
const CHECK_ANIMATION_MS = 1_000;

type ButtonStackRadioPillsProps = {
  heroVariantStyles?: StepInputRendererProps["heroVariantStyles"];
  onContinue: () => void;
  onFieldChange?: StepInputRendererProps["onFieldChange"];
  radioInput: RadioInputStepInput;
  stepName?: string;
};

function ButtonStackDivider({ label }: { label: string }) {
  return (
    // Rules are 64px on the 368px column and give up width before the label
    // wraps. The label keeps the middle, so extra space stays beside the
    // words instead of lengthening the rules.
    <span className="flex h-8 w-full items-center font-bold font-jakarta text-lg text-support-base">
      <span
        aria-hidden="true"
        className="h-px w-16 min-w-0 shrink bg-support-base"
      />
      <span className="flex-1 whitespace-nowrap px-4 text-center">{label}</span>
      <span
        aria-hidden="true"
        className="h-px w-16 min-w-0 shrink bg-support-base"
      />
    </span>
  );
}

/**
 * Same selection, analytics, and auto-advance path as RadioInput. The radio
 * label is the legend, drawn as the divider, so the pills stay a named group.
 */
function ButtonStackRadioPills({
  heroVariantStyles,
  onContinue,
  onFieldChange,
  radioInput,
  stepName,
}: ButtonStackRadioPillsProps) {
  const trackLeadForm = useTrackLeadFormEvent();
  const prefersReducedMotion = usePrefersReducedMotion();
  const {
    autoAdvance,
    fieldName,
    buttonLabel,
    helperText,
    helperTextSize,
    options,
    colorVariant,
  } = radioInput;
  const [selectedValue, selectValue] = usePersistedFieldState(
    fieldName,
    onFieldChange,
  );
  const helperId = useId();
  const continueTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const onContinueRef = useRef(onContinue);

  useEffect(() => {
    onContinueRef.current = onContinue;
  });

  useEffect(() => {
    return () => {
      if (continueTimerRef.current !== undefined) {
        clearTimeout(continueTimerRef.current);
      }
    };
  }, []);

  const handleSelect = (option: RadioInputStepInput["options"][number]) => {
    const value = option.value ?? option.label;
    const alreadySelected = value != null && selectedValue === value;

    if (autoAdvance) {
      const resolved = option.metricsLabel ?? option.value ?? option.label;
      if (stepName && fieldName && resolved) {
        trackLeadForm?.leadFormStep({
          stepName,
          userSelection: { [fieldName]: resolved },
        });
      }
    }

    selectValue(value ?? "");

    if (!autoAdvance) {
      return;
    }

    // Reduced motion shows the finished check immediately. Re-selecting a
    // pill whose animation already finished should continue at once too.
    if (
      prefersReducedMotion ||
      (alreadySelected && continueTimerRef.current === undefined)
    ) {
      onContinueRef.current();
      return;
    }

    if (alreadySelected) {
      return;
    }

    if (continueTimerRef.current !== undefined) {
      clearTimeout(continueTimerRef.current);
    }

    continueTimerRef.current = setTimeout(() => {
      continueTimerRef.current = undefined;
      onContinueRef.current();
    }, CHECK_ANIMATION_MS);
  };

  return (
    <fieldset
      aria-describedby={helperText ? helperId : undefined}
      className="flex w-full min-w-0 flex-col border-none p-0"
      role="radiogroup"
    >
      {buttonLabel ? (
        // A fieldset legend does not take part in flex gap, so the space
        // before the pills is margin on the legend.
        <legend className="mb-4 w-full max-w-full p-0">
          <ButtonStackDivider label={buttonLabel} />
        </legend>
      ) : null}
      {helperText ? (
        <p
          className={cn(
            heroVariantStyles?.helperTextColorClass ??
              defaultHeroVariantStyles.helperTextColorClass,
            resolveLabelFontFamilyClass(),
            resolveTextSizeClass(helperTextSize, "sm"),
          )}
          id={helperId}
        >
          {helperText}
        </p>
      ) : null}
      <div className="flex flex-col gap-4">
        {options.map((option) => {
          const value = option.value ?? option.label;
          const isSelected = value != null && selectedValue === value;

          return (
            <div
              className={cn(
                // Figma pills are 56px with 18px type. Hover classes are
                // exclusive: an unselected pill fills Clear Sky, and a selected
                // pill lightens to Tint 50. Both beat the outline button's
                // green hover because they target the label from this wrapper.
                "relative [&_label]:h-14 [&_label]:text-md",
                isSelected
                  ? "[&_label]:border-clear-sky-base [&_label]:bg-clear-sky-base [&_label]:pr-14 [&_label]:shadow-primary-button [&_label]:focus-within:border-clear-sky-base [&_label]:focus-within:bg-clear-sky-tints-500 [&_label]:hover:border-clear-sky-base [&_label]:hover:bg-clear-sky-tints-500"
                  : "[&_label]:bg-white [&_label]:focus-within:border-clear-sky-base [&_label]:focus-within:bg-clear-sky-base [&_label]:hover:border-clear-sky-base [&_label]:hover:bg-clear-sky-base [&_label]:hover:shadow-primary-button",
              )}
              key={option._key}
            >
              <RadioInputOption
                colorVariant={colorVariant}
                isSelected={isSelected}
                name={fieldName ?? ""}
                onChange={() => handleSelect(option)}
                option={option}
              />
              {isSelected ? (
                <div className="pointer-events-none absolute top-1/2 right-[11px] -translate-y-1/2">
                  <ButtonStackCheckBadge />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

export { ButtonStackRadioPills };
